import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getToken, setToken as persistToken, getIsAdmin, setIsAdmin as persistIsAdmin, clearAuth } from '../services/tokenStore';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getToken());
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(getIsAdmin());

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = getToken();
      const storedAdmin = getIsAdmin();

      if (storedAdmin) {
        setIsAdmin(true);
        setLoading(false);
        return;
      }

      if (storedToken) {
        try {
          const response = await axios.get(`${API}/user/profile`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          console.error('Auth init error:', error);
          clearAuth();
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
    // initAuth runs once on app mount; API/axios are module-level constants and
    // never change between renders, so they're intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (mobile, password) => {
    const response = await axios.post(`${API}/auth/login`, { mobile, password });
    const { token: newToken, user: userData } = response.data;
    persistToken(newToken);
    persistIsAdmin(false);
    setToken(newToken);
    setUser(userData);
    setIsAdmin(false);
    return userData;
  };

  const adminLogin = async (username, password) => {
    const response = await axios.post(`${API}/auth/admin-login`, { username, password });
    const { token: newToken } = response.data;
    persistToken(newToken);
    persistIsAdmin(true);
    setToken(newToken);
    setIsAdmin(true);
    return response.data;
  };

  const register = async (mobile, password, name, email, city, pincode) => {
    const response = await axios.post(`${API}/auth/create-password`, { mobile, password, name, email, city, pincode });
    const { token: newToken, user: userData } = response.data;
    persistToken(newToken);
    persistIsAdmin(false);
    setToken(newToken);
    setUser(userData);
    setIsAdmin(false);
    return userData;
  };

  const logout = () => {
    clearAuth();
    setToken(null);
    setUser(null);
    setIsAdmin(false);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const refreshUser = async () => {
    if (token && !isAdmin) {
      try {
        const response = await axios.get(`${API}/user/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        return response.data;
      } catch (error) {
        console.error('Refresh user error:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAdmin,
      login,
      adminLogin,
      register,
      logout,
      updateUser,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
